import { useEffect, useRef, useState } from 'react'
import { Icon } from '../ui/Icon'
import { paneUrl } from './paneMode'
import { dropSide, type DropSide, type Pane } from './panes'

/**
 * THE SPLIT STAGE — owner, 2026-08-22: 從 Side Panel 將一個元件拖到主顯示欄位, 並且
 * 選擇位置 (左, 右), 造成雙開, 或是多開的能力.
 *
 * Each pane is the whole app pointed at one conversation (see paneMode.ts for why an
 * iframe), so everything inside one — composer, terminal, scrollback, drafts — is the
 * real thing rather than a reduced copy.
 *
 * THE OVERLAY IS NOT DECORATION. An iframe swallows the pointer events of a drag that
 * started outside it: without a layer of our own above them, a drag from the rail would
 * cross the panes and never fire a single dragover. It exists ONLY while a drag is in
 * progress, because a permanent transparent layer over the stage would eat every click
 * meant for the conversation underneath.
 */
export function PaneStrip({
  panes,
  activeId,
  dragging,
  onActivate,
  onClose,
  onDropSel,
  onToggleRail,
  onFiles,
  onView,
  dock,
}: {
  panes: Pane[]
  activeId: string | null
  /** True while a conversation is being dragged out of the rail. */
  dragging: boolean
  onActivate: (id: string) => void
  onClose: (id: string) => void
  /** Where the drop landed: beside `onPaneId`, on `side`. */
  onDropSel: (onPaneId: string, side: DropSide) => void
  /**
   * Show or hide the rail — owner, 2026-08-22: 左上角的 Side Panel 彈出顯示按鈕應該要
   * 成為 Global 區域, 而非 Local 在每個分頁裡面.
   *
   * It lives here because the strip IS the global area while split: each pane is the
   * whole app and drew its own, so two panes meant two buttons, each trying to open a
   * rail that does not exist inside it.
   */
  onToggleRail: () => void
  /**
   * Open the file browser of the pane that has focus — owner, 2026-08-22: 讓 File
   * browser 的按鈕也提升到 Global, 並且隨著不同 Terminal 的 Focus 而改變所顯示的
   * Folder path 的內容.
   *
   * Takes the pane id rather than reading focus itself, so "which pane" is decided in
   * one place (the shell's `activePane`) and this stays a button.
   */
  onFiles: (paneId: string) => void
  /** A pane changed its own view; the shell records it as the seed for next time. */
  onView: (paneId: string, view: string) => void
  /**
   * The Files dock's contents, or null when it is closed.
   *
   * Given as an element rather than a selection: what belongs in there is the shell's
   * business, and this component's job is where it sits and how much room it takes.
   */
  dock: React.ReactNode
}) {
  const strip = useRef<HTMLDivElement>(null)
  /*
   * ONE `src` PER PANE, REBUILT ONLY WHEN THE CONVERSATION CHANGES.
   *
   * React writes `src` whenever the string changes, and a changed src is not a repaint
   * — it is a terminal disconnecting and a half-typed message going away. A pane's view
   * mode is recorded by the shell as a SEED for the next time that pane is created, so
   * folding it into the src would reload the pane every time somebody pressed the view
   * toggle, which is the opposite of remembering it.
   *
   * The conversation is a different matter: a rail tap replaces the focused pane, and
   * that is meant to navigate. So the cache is keyed on the selection and ignores the
   * view.
   */
  const srcs = useRef(new Map<string, { key: string; src: string }>())
  const srcFor = (p: Pane): string => {
    const key = paneUrl(p.sel, null)
    const had = srcs.current.get(p.id)
    if (had && had.key === key) return had.src
    const src = paneUrl(p.sel, p.view ?? null)
    srcs.current.set(p.id, { key, src })
    return src
  }
  const [hint, setHint] = useState<{ id: string; side: DropSide } | null>(null)

  /*
   * FOCUS HAS TO FOLLOW A CLICK **INSIDE** A PANE, and a pane is an iframe.
   *
   * `onPointerDownCapture` below catches the pane's own chrome and nothing else: a
   * pointer event inside an iframe does not cross the document boundary, so typing in
   * a pane's composer or clicking its terminal never moved focus. Invisible until a
   * rail tap started replacing the focused pane — then it meant the tap landed in
   * whichever pane was focused LAST, which is the worst kind of working control: it
   * does something, elsewhere.
   *
   * THE FIRST FIX WATCHED THE PARENT'S `blur` and read `document.activeElement`, which
   * the browser sets to the iframe element. That works exactly once. Going from one
   * pane straight to another — the commonest thing there is here — moves focus
   * iframe→iframe, and the parent window is ALREADY blurred, so it never blurs again
   * and nothing fires. It passed a test that focused a pane from the shell and failed
   * the moment the test did what a person does.
   *
   * So the pane says so itself. It is the same build from the same origin, it already
   * answers one message from the shell, and `event.source` identifies which frame spoke
   * without either side inventing an id.
   */
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const d = e.data as { wt?: string; view?: string } | null
      if (d?.wt !== 'focus' && d?.wt !== 'view') return
      const frames = strip.current?.querySelectorAll('iframe.pane-frame')
      for (const f of frames ?? []) {
        if ((f as HTMLIFrameElement).contentWindow !== e.source) continue
        const id = f.closest('[data-pane-id]')?.getAttribute('data-pane-id')
        if (!id) return
        if (d.wt === 'focus') onActivate(id)
        else if (d.view) onView(id, d.view)
        return
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [onActivate, onView])

  /** Which pane the pointer is over, and which half of it. */
  const targetAt = (clientX: number): { id: string; side: DropSide } | null => {
    const boxes = strip.current?.querySelectorAll('[data-pane-id]')
    if (!boxes) return null
    for (const el of boxes) {
      const r = el.getBoundingClientRect()
      if (clientX >= r.left && clientX <= r.right) {
        return { id: el.getAttribute('data-pane-id') ?? '', side: dropSide(clientX, r.left, r.width) }
      }
    }
    // Past the last pane — the person is aiming at the end of the row, not at nothing.
    const last = boxes[boxes.length - 1]
    return last ? { id: last.getAttribute('data-pane-id') ?? '', side: 'right' } : null
  }

  return (
    <div id="paneStrip" ref={strip}>
      {/*
       * A REAL GLOBAL ROW, not a button floating over the first pane.
       *
       * The first attempt put the toggle inside the strip at `left: 6px`, which lands
       * it in the top-left corner OF THE LEFTMOST PANE — visually identical to the
       * per-pane buttons it replaced, which is exactly what the owner asked to end. A
       * control that belongs to everything has to sit somewhere that belongs to
       * everything, and this row is that place.
       */}
      <div id="paneBar">
        <button type="button" id="paneRail" aria-label="Agents" title="Agents" onClick={onToggleRail}>
          <Icon name="menu" size={16} label="Agents" />
        </button>
        <button
          type="button"
          id="paneFiles"
          aria-label="Files"
          title="Files in the focused pane"
          /* Disabled rather than hidden when nothing is focused: a control that
             vanishes teaches people it was never there. */
          className={dock ? 'on' : undefined}
          disabled={!activeId}
          onClick={() => activeId && onFiles(activeId)}
        >
          <Icon name="folder" size={16} label="Files" />
        </button>
        <span id="paneBarCount">
          {panes.length} open
        </span>
      </div>
      <div id="paneRow">
      {panes.map((p) => (
        <section
          key={p.id}
          data-pane-id={p.id}
          className={`pane${p.id === activeId ? ' active' : ''}`}
          /* Capture, because the click lands inside an iframe that will not bubble out
             to us. Focus follows the pointer into a pane so the rail knows which one a
             new selection replaces. */
          onPointerDownCapture={() => onActivate(p.id)}
        >
          <header className="pane-hd">
            <span className="pane-grip" aria-hidden="true" />
            <button
              type="button"
              className="pane-x"
              aria-label="Close this pane"
              onClick={() => onClose(p.id)}
            >
              <Icon name="close" size={13} />
            </button>
          </header>
          {/*
           * `src` is set once per pane id. Re-deriving it on every render would reload
           * the conversation — and a reload here is not a repaint, it is a terminal
           * disconnecting and a composer losing what was half-typed in it.
           */}
          {/*
           * BUILT ONCE PER PANE. React writes `src` whenever the string changes, and a
           * changed src is not a repaint — it is a terminal disconnecting and a
           * half-typed message going away. The pane's own URL is the truth while it is
           * alive; `p.view` is only the seed for the next time this pane is created.
           */}
          <iframe className="pane-frame" title={`Pane ${p.id}`} src={srcFor(p)} />
        </section>
      ))}

        {dock && (
          <aside id="paneDock" aria-label="Files">
            {/*
             * THE BROWSER ITSELF, not another copy of the app.
             *
             * The first version put a whole app instance in here — owner: 按下 File
             * browser 按鈕居然會在右邊開啟一個新的 Session, 這是個脫褲子放屁的 Bug. He
             * is right, and my reason for it was wrong: I said the browser needs a
             * terminal socket to the machine it browses, and it does not. The socket is
             * used for ONE thing, the `cd` button (FileBrowser.tsx:161) — the listing
             * and the foreground check are ordinary relayed queries. So the dock is the
             * component, and `cd` is simply unavailable here, which it already knows how
             * to say.
             */}
            {dock}
          </aside>
        )}
      </div>

      {dragging && (
        <div
          id="paneDrop"
          role="presentation"
          onDragOver={(e) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            const t = targetAt(e.clientX)
            setHint((prev) => (prev && t && prev.id === t.id && prev.side === t.side ? prev : t))
          }}
          onDragLeave={() => setHint(null)}
          onDrop={(e) => {
            e.preventDefault()
            const t = targetAt(e.clientX)
            setHint(null)
            if (t) onDropSel(t.id, t.side)
          }}
        >
          {hint && (
            <div
              className="pane-hint"
              style={hintStyle(strip.current, hint)}
              aria-hidden="true"
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Where to draw the "it will land here" bar.
 *
 * Positioned from the real box rather than by index arithmetic, so it stays correct
 * when panes are different widths — which they will be as soon as splitters exist.
 */
function hintStyle(root: HTMLDivElement | null, hint: { id: string; side: DropSide }) {
  const el = root?.querySelector(`[data-pane-id="${CSS.escape(hint.id)}"]`)
  if (!el || !root) return { display: 'none' as const }
  const r = el.getBoundingClientRect()
  const base = root.getBoundingClientRect()
  return {
    left: `${(hint.side === 'left' ? r.left : r.right) - base.left}px`,
    top: '0px',
    height: `${base.height}px`,
  }
}
